import React, {
  createContext,
  useContext,
  type ReactNode,
  useState,
  useMemo,
} from "react";

import { useSubmitAiSearch } from "@/api/ai-search";
import { queryClient, QueryKeys } from "@/api/core";

interface AiSearchContextProps {
  handleInputChange(event: React.ChangeEvent<HTMLTextAreaElement>): void;
  handleSearch(): void;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  conversationId: string | null;
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  resetAiCache(): void;
}

const AiSearchContext = createContext({} as AiSearchContextProps);
const useAiSearchContext = () => useContext(AiSearchContext);

const AiSearchProvider = ({ children }: { children: ReactNode }) => {
  const [query, setQuery] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);

  const submitAiMutation = useSubmitAiSearch({ setConversationId });

  function handleSearch() {
    if (query.trim() !== "") {
      setQuery("");
      submitAiMutation.mutate({
        query,
        conversationId,
      });
    }
  }

  function handleInputChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setQuery(event.target.value);
  }

  function resetAiCache() {
    setQuery("");
    setConversationId(null);
    queryClient.removeQueries({ queryKey: QueryKeys.aiSearch.global });
  }

  const value = useMemo(
    () => ({
      query,
      setQuery,
      handleSearch,
      handleInputChange,
      conversationId,
      setConversationId,
      resetAiCache,
    }),
    [query, setQuery, handleSearch, handleInputChange, conversationId],
  );

  return (
    <AiSearchContext.Provider value={value}>
      {children}
    </AiSearchContext.Provider>
  );
};

export { useAiSearchContext };
export default AiSearchProvider;
